/* ============================================================================
   Product How to Use

   - Lazy loads video sources (data-src -> src) when in viewport
   - Click play overlay -> plays inline inside the card (matches live site)
   - Single-play: starting a video (or swiping the mobile slider) resets
     every other card back to its idle state — only one video plays at a time
   - Pause / end / "another card activated" -> return to the page-load look
     (poster background visible, default play SVG visible, video rewound)
   - Mobile Swiper carousel
   ========================================================================== */

(function () {
  'use strict';

  function initAll() {
    document.querySelectorAll('.phtu-section').forEach(init);
  }

  function init(root) {
    root.querySelectorAll('[data-phtu-video] video').forEach(primeVideoFrame);
    lazyLoadVideos(root);
    initInlineCardVideo(root);
    initMobileSwiper(root);
  }

  function primeVideoFrame(video) {
    var wrap = video.closest('[data-phtu-video]');
    video.querySelectorAll('source[data-src]').forEach(function (src) {
      var url = src.getAttribute('data-src');
      if (url && !src.getAttribute('src')) src.setAttribute('src', url);
    });
    video.load();
    video.addEventListener('loadeddata', function () {
      video.muted = true;
      video.play().then(function () {
        video.pause();
        video.currentTime = 0;
        if (wrap) wrap.classList.add('is-ready');
      }).catch(function () {
        var poster = video.getAttribute('data-poster');
        if (poster) video.setAttribute('poster', poster);
        if (wrap) wrap.classList.add('is-ready');
      });
    }, { once: true });
  }

  /**
   * Return a video card to its page-load idle state:
   *   - drop `is-playing` (so CSS reveals the `.phtu-play` SVG again)
   *   - restore the poster `background-image` cached at init time
   *   - pause the underlying <video> and rewind to frame 0 so the next
   *     play starts from the beginning, instead of the previous freeze-frame
   * Safe to call on a card that is already idle.
   */
  function resetCardToIdle(wrap) {
    if (!wrap) return;
    wrap.classList.remove('is-playing');
    var video = wrap.querySelector('video');
    if (video) {
      try { video.pause(); } catch (e) { /* ignore */ }
      try { if (video.currentTime !== 0) video.currentTime = 0; } catch (e) { /* ignore */ }
    }
  }

  function lazyLoadVideos(root) {
    var sources = root.querySelectorAll('source[data-src]');
    function promote(src) {
      var url = src.getAttribute('data-src');
      if (url && !src.getAttribute('src')) {
        src.setAttribute('src', url);
        var video = src.parentElement;
        if (video && typeof video.load === 'function') {
          /* preload="none" in the markup keeps initial page load light, but it
             also makes load() a no-op network-wise — the first tap would stall
             while the file downloads. Once a card is near the viewport, allow
             buffering so play starts instantly (parity with live). */
          video.preload = 'auto';
          video.load();
        }
      }
    }
    if (!('IntersectionObserver' in window)) {
      sources.forEach(promote);
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.querySelectorAll('source[data-src]').forEach(promote);
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '250px' });
    /* Observe the card wrap, not the <video>: the video sits at opacity 0
       inside the swiper's transformed subtree and never reports an
       intersection, so video-based observation left preload="none" videos
       with no source until the first tap (slow start). The wrap is the
       visible poster element and intersects reliably. */
    root.querySelectorAll('[data-phtu-video]').forEach(function (wrap) {
      observer.observe(wrap);
    });
  }

  function promoteVideoSources(video) {
    var promoted = false;
    video.querySelectorAll('source[data-src]').forEach(function (src) {
      var url = src.getAttribute('data-src');
      if (url && !src.getAttribute('src')) {
        src.setAttribute('src', url);
        promoted = true;
      }
    });
    if (promoted && typeof video.load === 'function') {
      video.preload = 'auto';
      video.load();
    }
  }

  function initInlineCardVideo(root) {
    var allCards = Array.prototype.slice.call(root.querySelectorAll('[data-phtu-video]'));

    function activate(wrap) {
      var video = wrap.querySelector('video');
      if (!video) return;

      promoteVideoSources(video);

      /* Single-play rule: reset every other card so only this video plays.
         Resetting (vs. just pausing) ensures the previously-playing card
         shows its poster + play SVG again, matching the page-load look. */
      allCards.forEach(function (other) {
        if (other !== wrap) resetCardToIdle(other);
      });

      wrap.classList.add('is-playing');
      video.removeAttribute('controls');
      video.controls = false;
      video.setAttribute('playsinline', '');
      video.muted = false;

      var p = video.play();
      if (p && typeof p.then === 'function') {
        p.catch(function () {
          video.muted = true;
          video.play().catch(function () {});
        });
      }
    }

    allCards.forEach(function (wrap) {
      if (wrap.__phtuInlineBound) return;
      wrap.__phtuInlineBound = true;

      var prefetch = function () {
        var video = wrap.querySelector('video');
        if (video) promoteVideoSources(video);
      };
      wrap.addEventListener('pointerenter', prefetch);
      wrap.addEventListener('touchstart', prefetch, { passive: true });

      wrap.addEventListener('click', function (e) {
        var video = wrap.querySelector('video');
        if (!video) return;

        /* Click on a playing card → pause and return to the idle state
           (poster background + default play SVG visible), matching the
           load-time look. */
        if (wrap.classList.contains('is-playing')) {
          if (video.contains(e.target)) {
            e.preventDefault();
            resetCardToIdle(wrap);
          }
          return;
        }

        activate(wrap);
      });

      wrap.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        if (wrap.classList.contains('is-playing')) {
          resetCardToIdle(wrap);
        } else {
          activate(wrap);
        }
      });
      if (!wrap.hasAttribute('tabindex')) wrap.setAttribute('tabindex', '0');
      if (!wrap.hasAttribute('role')) wrap.setAttribute('role', 'button');
      var card = wrap.closest('.phtu-card');
      var titleEl = card ? card.querySelector('.phtu-info__title') : null;
      var ariaLabel = titleEl && titleEl.textContent ? titleEl.textContent.trim() + ' — play video' : 'Play video';
      wrap.setAttribute('aria-label', ariaLabel);

      /* Defensive: if `loop` is ever removed in the future, reset on end. */
      var v = wrap.querySelector('video');
      if (v) {
        v.addEventListener('ended', function () { resetCardToIdle(wrap); });
      }
    });
  }

  function initMobileSwiper(root) {
    var container = root.querySelector('[data-phtu-swiper]');
    if (!container) return;
    function build() {
      if (typeof window.Swiper !== 'function') { setTimeout(build, 60); return; }
      new window.Swiper(container, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: true,
        navigation: {
          prevEl: container.querySelector('[data-phtu-prev]'),
          nextEl: container.querySelector('[data-phtu-next]'),
          disabledClass: 'is-disabled'
        },
        on: {
          /* Swiping to a new slide should stop the previous card and
             return it to its idle (poster + play SVG) state, so we never
             leave a video playing off-screen. */
          slideChange: function () {
            root.querySelectorAll('[data-phtu-video]').forEach(resetCardToIdle);
          }
        }
      });
      /* loop: true clones the first/last slides AFTER the original cards were
         bound. Clones are plain DOM copies — no listeners, no cached poster —
         so without this rebind their videos can't play and resets blank their
         posters. initInlineCardVideo is idempotent per card. */
      initInlineCardVideo(root);
      root.querySelectorAll('[data-phtu-video] video').forEach(primeVideoFrame);
      lazyLoadVideos(root);
    }
    build();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
